import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { PrivacyCheckbox } from "@/components/legal/PrivacyCheckbox";
import { buttonClass } from "@/components/ui/buttonStyles";
import type { ResourcesRegistration } from "@/utils/resourcesAccess";
import { useAppTranslation } from "@/i18n/useAppTranslation";

type Props = {
  titleId: string;
  resourceTitle?: string;
  defaultValues?: ResourcesRegistration;
  onRegistered: (data: ResourcesRegistration) => Promise<void>;
  onDismiss: () => void;
};

const inputCls =
  "w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-base text-ink outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40 aria-[invalid=true]:border-red-700";

/** Native modal dialog: focus stays inside, Escape closes, the page behind is inert. */
export function ResourceRegistrationModal({ titleId, resourceTitle, defaultValues, onRegistered, onDismiss }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameFieldId = useId();
  const emailFieldId = useId();
  const errorId = useId();
  const { t } = useAppTranslation("resourcesPage");
  const { t: tCommon } = useAppTranslation("common");
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const submitting = submitState === "submitting";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!privacyAccepted) return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
    if (name.length < 2 || !emailOk) {
      setSubmitState("error");
      return;
    }
    setSubmitState("submitting");
    try {
      await onRegistered({ name, email });
      setSubmitState("idle");
      form.reset();
    } catch {
      setSubmitState("error");
    }
  }

  const trimmedTitle = resourceTitle?.trim();
  const invalid = submitState === "error";

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        if (!submitting) onDismiss();
      }}
      onClick={(e) => {
        if (e.target === dialogRef.current && !submitting) onDismiss();
      }}
      className="m-auto w-[calc(100%-2rem)] max-w-md rounded-[1.75rem] bg-white p-0 text-ink shadow-lit-lg backdrop:bg-brand-primary/55 backdrop:backdrop-blur-sm"
    >
      <div className="relative p-7 pt-12 sm:p-9 sm:pt-12">
        <button
          type="button"
          onClick={onDismiss}
          disabled={submitting}
          aria-label={t("closeForm")}
          className="absolute right-3 top-3 grid size-11 place-items-center rounded-full text-ink-muted transition-colors hover:bg-teal-soft hover:text-brand-primary disabled:opacity-40"
        >
          <X className="size-5" aria-hidden="true" />
        </button>
        <h2 id={titleId} className="pr-8 font-display text-2xl font-semibold text-brand-primary sm:text-3xl">
          {t("modalTitle")}
        </h2>
        <p className="mt-2 text-ink-soft">
          {trimmedTitle?.length ? t("modalIntroWithTitle", { title: trimmedTitle }) : t("modalIntro")}
        </p>
        <form onSubmit={handleSubmit} noValidate className="mt-6 flex flex-col gap-4">
          <div>
            <label htmlFor={nameFieldId} className="mb-1.5 block text-sm font-semibold text-brand-primary">
              {t("name")}
            </label>
            <input
              id={nameFieldId}
              name="name"
              type="text"
              autoComplete="name"
              required
              minLength={2}
              defaultValue={defaultValues?.name ?? ""}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor={emailFieldId} className="mb-1.5 block text-sm font-semibold text-brand-primary">
              {t("email")}
            </label>
            <input
              id={emailFieldId}
              name="email"
              type="email"
              autoComplete="email"
              required
              defaultValue={defaultValues?.email ?? ""}
              aria-invalid={invalid}
              aria-describedby={invalid ? errorId : undefined}
              className={inputCls}
            />
          </div>
          <PrivacyCheckbox
            checked={privacyAccepted}
            onChange={setPrivacyAccepted}
            disabled={submitting}
            suffix={t("privacySuffix")}
          />
          {invalid ? (
            <p id={errorId} className="text-sm font-medium text-red-700" role="alert">
              {t("validationError")}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting || !privacyAccepted}
            className={buttonClass("primary", "lg", "w-full")}
          >
            {submitting ? tCommon("sending") : t("downloadSubmit")}
          </button>
        </form>
      </div>
    </dialog>
  );
}
